import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormSwitch } from "@actinc/dls";
import {
  AccountMultiplePlus as AccountMultiplePlusIcon,
  AccountMultipleRemove as AccountMultipleRemoveIcon,
} from "@actinc/dls/icons";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useMutation, useQuery } from "urql";

import {
  Backdrop,
  DataGrid,
  PreviewContent,
  SelectUserDialog,
} from "../../../components";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";

import { useSessionStore } from "../../../services/hooks";

import {
  getSelectOptionsFromMetadata,
  getTaskOptions,
  getUserOptions,
  PREVIEW_TYPES,
  setTitle,
} from "../../../services/utils";

import { TaskDetailsDialog } from "../Tasks/TaskDetailsDialog";

const PoolTasks = () => {
  const datagridKey = "poolTasks";
  const [datagridModels = {}] = useSessionStore("datagrid");

  const page = useRef(0);
  const pagesToNextCursors = useRef([]);

  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);

  const [assignTasksResponse, assignTasks] = useMutation(assignTasksMutation);
  const [unassignTasksResponse, unassignTasks] = useMutation(
    unassignTasksMutation,
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectUserDialogProps, setSelectUserDialogProps] = useState({
    open: false,
  });
  const [showAssignedTasks, setShowAssignedTasks] = useState(false);

  const [dialogState, setDialogState] = useState({
    dialogName: "",
    open: false,
  });

  const onClose = () => {
    setDialogState({
      dialogName: "",
      open: false,
    });
  };

  const [getTasksByPoolsResponse, refreshTasksByPools] = useQuery({
    query: getTasksByPoolsQuery,
    pause:
      !datagridModels[datagridKey]?.filterModel?.items.every(
        (filter) =>
          ["isEmpty", "isNotEmpty"].includes(filter.operator) ||
          (typeof filter.value !== "undefined" &&
            !(Array.isArray(filter.value) && filter.value.length === 0)),
      ) ||
      !paginationModel.pageSize ||
      (page.current !== 0 && !pagesToNextCursors.current[page.current - 1]) ||
      !datagridModels[datagridKey]?.sortModel,
    variables: {
      filterModel: {
        items: datagridModels[datagridKey]?.filterModel.items.map(
          ({ field, id, operator, value }) => ({
            field,
            id,
            operator,
            value:
              value instanceof Date
                ? value.toISOString()
                : typeof value === "object"
                  ? JSON.stringify(value)
                  : value,
          }),
        ),
        logicOperator: datagridModels[datagridKey]?.filterModel.logicOperator,
      },
      includeAssignedTasks: showAssignedTasks,
      paginationModel: {
        cursor: pagesToNextCursors.current[page.current - 1],
        pageSize: paginationModel.pageSize,
      },
      sortModel: datagridModels[datagridKey]?.sortModel,
    },
  });

  const { data, error, fetching } = getTasksByPoolsResponse;

  const rows = useMemo(() => {
    return data?.tasksByPools?.rows || [];
  }, [data?.tasksByPools?.rows]);

  const selectedTasks = useMemo(
    () => rows.filter((row) => rowSelectionModel.includes(row.id)),
    [rows, rowSelectionModel],
  );

  useEffect(() => {
    if (
      !data?.tasksByPools.pageInfo.nextCursor ||
      pagesToNextCursors.current[page.current]
    )
      return;
    pagesToNextCursors.current[page.current] =
      data.tasksByPools.pageInfo.nextCursor;
  }, [data?.tasksByPools.pageInfo.nextCursor]);

  useEffect(() => {
    if (getTasksByPoolsResponse.stale) {
      refreshTasksByPools();
    }
  }, [getTasksByPoolsResponse.stale, refreshTasksByPools]);

  useEffect(() => {
    setTitle(["CDE", "Task Pool"]);
  }, []);

  const openTaskDetailsDialog =
    ({ createdDateTime, description, imageDetails, item, passage, ...rest }) =>
    () => {
      setDialogState({
        dialogName: "taskDetails",
        open: true,
        objectType: item ? "Item" : passage ? "Passage" : "",
        row: {
          contentArea: item?.contentArea || passage?.contentArea,
          createdDateTime: new Date(createdDateTime),
          gradeLevel: item?.gradeLevel || passage?.gradeLevel,
          imageDetails,
          itemFormat: item?.format,
          name: item?.name || passage?.name,
          program: programs.find(
            ({ id }) => id === item?.program || passage?.program,
          )?.name,
          requestedDateTime: new Date(rest.requestedDateTime),
          taskName: rest?.name,
        },
      });
    };

  const handleSelectionModelChange = useCallback((selection) => {
    setRowSelectionModel(selection);
  }, []);

  const isRowSelectable = useCallback(
    (tasks) => (params) => {
      const programID = params.row.program;
      return (
        rowSelectionModel.includes(params.id) ||
        rowSelectionModel.every((taskID) => {
          const task = tasks.find(({ id }) => id === taskID);
          const taskProgram = task.program;
          return (
            taskProgram === programID &&
            task.assignedPools.every((pool) =>
              params.row.assignedPools.includes(pool),
            ) &&
            params.row.assignedPools.every((pool) =>
              task.assignedPools.includes(pool),
            ) &&
            task._actions.every((action) =>
              params.row._actions.includes(action),
            ) &&
            params.row._actions.every((action) =>
              task._actions.includes(action),
            )
          );
        })
      );
    },
    [rowSelectionModel],
  );

  const onModelChange = useCallback(() => {
    page.current = 0;
    pagesToNextCursors.current = [];
    setPaginationModel({ ...paginationModel, page: 0 });
  }, [paginationModel]);

  const onPaginationModelChange = useCallback(
    (model) => {
      if (model.pageSize !== paginationModel.pageSize) {
        page.current = 0;
        pagesToNextCursors.current = [];
        setPaginationModel({ ...model, page: 0 });
      } else {
        page.current = model.page;
        setPaginationModel(model);
      }
    },
    [paginationModel],
  );

  const openAssignDialog =
    (users = []) =>
    (tasks = []) =>
    (selection = []) =>
    (row) =>
    () => {
      const taskIDs = row ? [row.id] : selection;
      const selected = tasks.filter((task) => taskIDs.includes(task.id));
      const match = selected[0];
      const programs = [match?.program];
      const roles = [
        ...new Set(selected.map((task) => task.assignedPools).flat()),
      ];
      setSelectUserDialogProps({
        onClose: (assignedUser) => {
          setSelectUserDialogProps({ open: false });
          if (!assignedUser) return;
          setRowSelectionModel([]);
          assignTasks({ assignedUser, taskIDs });
        },
        open: true,
        users: getUserOptions(users)({ activeOnly: true, programs, roles }),
      });
    };

  const unassignUser =
    (selection = []) =>
    (row) =>
    () => {
      const taskIDs = row ? [row.id] : selection;
      setRowSelectionModel([]);
      unassignTasks({ taskIDs });
    };

  const taskNameOptions = useMemo(() => getTaskOptions(), []);

  const contentAreaOptions = useMemo(() => {
    const itemOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("contentArea");
    const passageOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("contentArea");
    return [...itemOptions, ...passageOptions]
      .map(({ stringValue }) => stringValue)
      .filter((value, index, array) => array.indexOf(value) === index)
      .sort();
  }, [metadataConfiguration, programs]);

  const developmentStateOptions = useMemo(() => {
    const itemOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("developmentState");
    const passageOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("developmentState");
    return [...itemOptions, ...passageOptions]
      .map(({ stringValue }) => stringValue)
      .filter((value, index, array) => array.indexOf(value) === index)
      .sort();
  }, [metadataConfiguration, programs]);

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        flex: 1,
        headerName: "Task Name",
        minWidth: 150,
        renderCell: ({ row, value }) => {
          const passageOnly = !row?.item?.id && !!row?.passage?.id;
          return (
            !!value && (
              <>
                {value}
                <PreviewContent
                  objects={
                    passageOnly
                      ? [
                          {
                            id: row?.passage?.id,
                            versionID: row?.passage?.versionID,
                          },
                        ]
                      : [{ id: row?.item?.id, versionID: row?.item?.versionID }]
                  }
                  objectType={passageOnly ? "Passage" : "Item"}
                  previewTypes={PREVIEW_TYPES}
                />
              </>
            )
          );
        },
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueOptions: taskNameOptions,
      },
      {
        field: "passageName",
        flex: 2,
        headerName: "Passage Name",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) =>
          row.passage?.name || row.item?.passage?.name || "",
      },
      {
        field: "itemName",
        flex: 2,
        headerName: "Item Name",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) => row.item?.name || "",
      },
      {
        field: "description",
        flex: 1,
        headerName: "Description",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) => row.item?.description || "",
      },
      {
        field: "contentArea",
        headerName: "Content Area",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) =>
          row.item?.contentArea || row.passage?.contentArea || "",
        valueOptions: contentAreaOptions,
      },
      {
        field: "programName",
        headerName: "Program",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) => {
          const program = programs.find(({ id }) => row.program === id);
          return program?.name || "";
        },
        valueOptions: programs.map(({ name }) => name),
      },
      {
        field: "developmentState",
        headerName: "Development State",
        minWidth: 200,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) =>
          row.item?.developmentState || row.passage?.developmentState || "",
        valueOptions: developmentStateOptions,
      },
      {
        field: "createdDateTime",
        headerName: "Created Date",
        minWidth: 150,
        sortComparator: (v1, v2) => {
          const str1 = v1.valueOf() || 0;
          const str2 = v2.valueOf() || 0;
          return str1 - str2;
        },
        type: "date",
        valueGetter: ({ row }) =>
          row.createdDateTime ? new Date(row.createdDateTime) : "",
      },
      {
        field: "assignedUser",
        headerName: "Assigned To",
        minWidth: 150,
        type: "singleSelect",
        valueFormatter: ({ value }) => {
          const user = users.find(({ id }) => id === value);
          return user?.name || "";
        },
        valueGetter: ({ row }) => row.assignedUser || "",
        valueOptions: users.map(({ id, name }) => ({ value: id, label: name })),
      },
      {
        field: "assignedDateTime",
        headerName: "Assigned",
        minWidth: 150,
        sortComparator: (v1, v2) => {
          const str1 = v1.valueOf() || 0;
          const str2 = v2.valueOf() || 0;
          return str1 - str2;
        },
        type: "date",
        valueGetter: ({ row }) =>
          row.assignedDateTime ? new Date(row.assignedDateTime) : "",
      },
    ];
  }, [
    contentAreaOptions,
    developmentStateOptions,
    programs,
    taskNameOptions,
    users,
  ]);

  const dataGridProps = {
    actions: [
      {
        disabled: (row) => !row._actions?.includes("assign"),
        label: "Assign",
        onClick: openAssignDialog(users)(rows)(rowSelectionModel),
      },
      {
        label: "Task Details",
        onClick: openTaskDetailsDialog,
      },
      {
        disabled: (row) => !row._actions?.includes("unassign"),
        label: "Unassign",
        onClick: unassignUser(rowSelectionModel),
      },
    ],
    checkboxSelection: true,
    columns,
    datagridKey,
    disableRowSelectionOnClick: true,
    disableSelectAllCheckbox: true,
    error: error?.errorInfo?.message,
    filterMode: "server",
    filterModel: { items: [], logicOperator: "and" },
    isRowSelectable: isRowSelectable(rows),
    onFilterModelChange: onModelChange,
    onPaginationModelChange,
    onRowSelectionModelChange: handleSelectionModelChange,
    onSortModelChange: onModelChange,
    paginationMode: "server",
    paginationModel,
    rowCount: data?.tasksByPools.rowCount || 0,
    rows,
    rowSelectionModel,
    sortModel: [{ field: "name", sort: "asc" }],
    sortingMode: "server",
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Pool Tasks</Typography>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          {!!rowSelectionModel.length &&
            selectedTasks.every((task) => task._actions.includes("assign")) && (
              <Button
                onClick={openAssignDialog(users)(rows)(rowSelectionModel)()}
                size="small"
                startIcon={<AccountMultiplePlusIcon />}
                sx={{ marginLeft: 2 }}
                variant="outlined"
              >
                Assign Selected Tasks
              </Button>
            )}
          {!!rowSelectionModel.length &&
            selectedTasks.every((task) =>
              task._actions.includes("unassign"),
            ) && (
              <Button
                onClick={unassignUser(rowSelectionModel)()}
                size="small"
                startIcon={<AccountMultipleRemoveIcon />}
                sx={{ marginLeft: 2 }}
                variant="outlined"
              >
                Unassign Selected Tasks
              </Button>
            )}
          <FormSwitch
            checked={showAssignedTasks}
            id="viewAssignedTasks"
            label="View Assigned Tasks"
            onClick={() => setShowAssignedTasks(!showAssignedTasks)}
            sx={{ marginLeft: 2 }}
          />
        </Box>
      </Box>

      {fetching && <Backdrop loading={fetching} />}
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginX: 2,
          marginY: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <DataGrid {...dataGridProps}></DataGrid>
        </Box>
      </Paper>

      {dialogState.open && dialogState.dialogName === "taskDetails" && (
        <TaskDetailsDialog {...dialogState} onClose={onClose} />
      )}
      {selectUserDialogProps.open && (
        <SelectUserDialog {...selectUserDialogProps} />
      )}
    </>
  );
};

const getTasksByPoolsQuery = `
  query GetTasksByPools($filterModel: DataGridFilterModelInput!, $includeAssignedTasks: Boolean!, $paginationModel: DataGridPaginationModelInput!, $sortModel: [DataGridSortingItemInput!]) {
    tasksByPools(filterModel: $filterModel, includeAssignedTasks: $includeAssignedTasks, paginationModel: $paginationModel, sortModel: $sortModel) {
      pageInfo {
        hasNextPage
        nextCursor
        truncated
      }
      rowCount
      rows {
        _actions
        assignedDateTime
        assignedPools
        assignedUser
        createdDateTime
        dueDate
        id
        imageDetails {
          component
          description
          file
          format
          type
        }
        item {
          contentArea
          description
          developmentState
          format
          gradeLevel
          id
          name
          passage {
            name
          }
          program
          versionID
        }
        name
        notes
        passage {
          contentArea
          developmentState
          gradeLevel
          id
          name
          program
          versionID
        }
        program
        requestedDateTime
        taskDefinition {
          assignment {
            roles
          }
        }
      }
    }
  }`;

const assignTasksMutation = `
  mutation AssignTasks($assignedUser: ID!, $taskIDs: [ID!]!) {
    assignPoolTasks(assignedUser: $assignedUser, taskIDs: $taskIDs) {
      assignedDateTime
      assignedUser
      id
    }
  }`;

const unassignTasksMutation = `
  mutation UnassignTasks($taskIDs: [ID!]!) {
    unassignPoolTasks(taskIDs: $taskIDs) {
      id
    }
  }`;

export { PoolTasks, getTasksByPoolsQuery };
